import React from 'react'
import { InferGetStaticPropsType } from 'next'
import { useBreakpointValue } from '@hub/design-system-base'
import { localeToCountry } from '@scentregroup/shared/helpers/local-to-country'
import Layout from '@scentregroup/shared/hub-components/layout'
import { fetchNationalStaticData } from '@scentregroup/shared/hub-components/universal-header'
import * as withNationalStoryDetail from '@scentregroup/shared/containers/with-national-story-detail/index.js'
import { StoryDetail } from '@scentregroup/shared/pages/centre/entry-detail'
import FeaturedContent from '@scentregroup/shared/components/featured-content'
import { DeckCard } from '@scentregroup/shared/components/deck'
import { MarketplaceDeckContainer } from '@scentregroup/shared/components/marketplace-deck-container'
import { TouchEventDetection } from '@scentregroup/shared/helpers/with-touch-detection'
import { theEditDeckClickTracker } from '@scentregroup/shared/pages/index.analytics'

import { REVALIDATION_PERIODS } from '@scentregroup/shared/constants'
import { GetStaticProps, GetStaticPropsContext } from '../../../types/next'
import { makeApolloClient } from '../../../lib/apolloClient'
import { GraphQLHocProvider } from '../../../lib/graphql-hoc-provider'
import { staticHocFetch } from '../../../lib/static-hoc-fetch'
import { propsOrNotFound } from '../../../lib/props-or-not-found'

export const getStaticPaths = async () => ({
  // TODO: Generate popular paths?
  paths: process.env.SKIP_STATIC_PATHS ? [] : [],
  fallback: 'blocking',
})

export const getStaticProps: GetStaticProps = async ({
  params = {},
  locale,
}: GetStaticPropsContext) => {
  const country = localeToCountry(locale)

  const { id, slug } = params

  const client = makeApolloClient()

  const [withNationalStoryDetailProps, headerStaticData] = await Promise.all([
    staticHocFetch({
      ...withNationalStoryDetail,
      client,
      props: { id, country },
    }),
    await fetchNationalStaticData(client, { country }),
  ])

  return propsOrNotFound({
    props: {
      country,
      slug,
      headerStaticData,
      // Why isn't this 'with-national-story-detail'? Because we're co-opting
      // the <StoryDetail> component which expects the data to be coming in
      // within the `with-story-detail` container.
      // But what about the legacy site? It'll still try to fetch the data
      // with 'with-story-detail', which work... Nope; there's no route for a
      // "national story", so this will only ever be rendered from the Next.js
      // site.
      'with-story-detail': withNationalStoryDetailProps,
    },
    revalidate: REVALIDATION_PERIODS.static,
  })
}

export function NationalStory({
  country,
  relatedStories,
  headerStaticData,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const headerHasBottomMargin = useBreakpointValue({ base: false, sm: true })
  return (
    <Layout
      country={country}
      centre={undefined}
      header={{
        variant: 'national',
        data: headerStaticData,
        hasBottomMargin: headerHasBottomMargin,
      }}
    >
      <StoryDetail
        // @ts-ignore
        isEvolved
        country={country}
      />
      {relatedStories?.length ? (
        <MarketplaceDeckContainer>
          <TouchEventDetection>
            <FeaturedContent isEvolved title="The Edit">
              {relatedStories.map((relatedStory: any, index: number) => (
                <DeckCard
                  externalLink={relatedStory.externalLink}
                  link={relatedStory.url}
                  key={index}
                  image={relatedStory.imageUrl}
                  title={relatedStory.title}
                  body={relatedStory.body}
                  trackers={{ clicked: theEditDeckClickTracker }}
                />
              ))}
            </FeaturedContent>
          </TouchEventDetection>
        </MarketplaceDeckContainer>
      ) : null}
    </Layout>
  )
}

const StoryDetailPage = (
  props: InferGetStaticPropsType<typeof getStaticProps>
) => (
  <GraphQLHocProvider value={props}>
    <NationalStory
      {...props['with-story-detail']}
      country={props.country}
      headerStaticData={props.headerStaticData}
    />
  </GraphQLHocProvider>
)

export default StoryDetailPage
